import { AirHandlerOptionsSummaryType } from '@alpinehomeair/common';
import { useAppSelector } from 'app/hooks';
import { useMemo } from 'react';
import {
  getEffectivePerZoneTypes,
  isAirHandlerAvailabilityUnknown,
  isPositionedWithZoneItems
} from 'utils/air-handler-availability';
import {
  foldCondenserPool,
  matchingPoolSystemCount
} from 'utils/air-handler-pool-tuples';

export type MetricsRange = { min: number; max: number };

export type AirHandlerMetrics = {
  priceRange: MetricsRange | null;
  seer2Range: MetricsRange | null;
  btuRange: MetricsRange | null;
  totalSystems: number;
  // Systems compatible with the placed heads; null when a condenser's options
  // carry no matching information (pre-tuples server response).
  matchingSystems: number | null;
};

type CondenserMetricsSource = {
  summary: AirHandlerOptionsSummaryType;
  matchingCount: number | null;
};

type RangePair = { min: number | null; max: number | null };

function sumRange(pairs: RangePair[]): MetricsRange | null {
  const valid = pairs.filter(
    (p): p is { min: number; max: number } => p.min != null && p.max != null
  );
  if (valid.length === 0) return null;
  return {
    min: valid.reduce((acc, p) => acc + p.min, 0),
    max: valid.reduce((acc, p) => acc + p.max, 0)
  };
}

function unionRange(pairs: RangePair[]): MetricsRange | null {
  const mins = pairs.map((p) => p.min).filter((v): v is number => v != null);
  const maxes = pairs.map((p) => p.max).filter((v): v is number => v != null);
  if (mins.length === 0 && maxes.length === 0) return null;
  return {
    min: mins.length > 0 ? Math.min(...mins) : Math.min(...maxes),
    max: maxes.length > 0 ? Math.max(...maxes) : Math.max(...mins)
  };
}

function aggregateMetrics(
  sources: CondenserMetricsSource[]
): AirHandlerMetrics {
  const summaries = sources.map((source) => source.summary);
  const matchingCounts = sources.map((source) => source.matchingCount);
  return {
    priceRange: sumRange(
      summaries.map((s) => ({ min: s.minPrice, max: s.maxPrice }))
    ),
    seer2Range: unionRange(
      summaries.map((s) => ({ min: s.minSEER2, max: s.maxSEER2 }))
    ),
    btuRange: sumRange(
      summaries.map((s) => ({ min: s.minCondenser, max: s.maxCondenser }))
    ),
    totalSystems: summaries.reduce((acc, s) => acc + s.count, 0),
    matchingSystems: matchingCounts.every(
      (count): count is number => count !== null
    )
      ? matchingCounts.reduce((acc, count) => acc + count, 0)
      : null
  };
}

// A current local pool folds the whole-pool aggregates and the matching
// count against the placed heads; otherwise the server summary stands.
function condenserMetricsSource(
  condenser: Parameters<typeof foldCondenserPool>[0]
): CondenserMetricsSource | null {
  const options = condenser.airHandlerOptions;
  if (!options) return null;
  const folded = foldCondenserPool(condenser);
  return {
    summary: folded?.all ?? options.summary,
    matchingCount: matchingPoolSystemCount(condenser)
  };
}

export default function useMatchingAirHandlerMetrics() {
  const { configurations, activeConfig } = useAppSelector(
    (state) => state.configurations
  );
  const activeConfiguration = configurations[activeConfig];

  const positionedCondensersWithItems = useMemo(() => {
    if (!activeConfiguration) return [];
    return activeConfiguration.condensers.filter(isPositionedWithZoneItems);
  }, [activeConfiguration]);

  const metricsSources = useMemo(
    () =>
      positionedCondensersWithItems
        .map(condenserMetricsSource)
        .filter((source): source is CondenserMetricsSource => source !== null),
    [positionedCondensersWithItems]
  );

  const metrics = useMemo(() => {
    if (metricsSources.length === 0) return null;
    return aggregateMetrics(metricsSources);
  }, [metricsSources]);

  const availableAirHandlers = useMemo<Record<string, string[]>>(() => {
    const combined: Record<string, string[]> = {};
    positionedCondensersWithItems.forEach((condenser) => {
      const perZoneTypes = getEffectivePerZoneTypes(condenser);
      if (!perZoneTypes) return;
      Object.entries(perZoneTypes).forEach(([zoneId, codes]) => {
        const existing = new Set(combined[zoneId] ?? []);
        codes.forEach((code) => existing.add(code));
        combined[zoneId] = [...existing];
      });
    });
    return combined;
  }, [positionedCondensersWithItems]);

  const isLoading = useMemo(
    () => positionedCondensersWithItems.some(isAirHandlerAvailabilityUnknown),
    [positionedCondensersWithItems]
  );

  return { metrics, availableAirHandlers, isLoading };
}
